import { useEffect, useState } from "react";
import { Monitor, Smartphone, Tv, Download as DownloadIcon, Check } from "lucide-react";
import { toast } from "sonner";

const PLATFORMS = [
  { icon: Monitor, title: "Windows PC", steps: "Open NeTimeTV in Edge or Chrome, click the Install icon in the address bar (or use the button below)." },
  { icon: Smartphone, title: "iPhone & Android", steps: "iPhone: Share → Add to Home Screen. Android: tap the Install button or browser menu → Install app." },
  { icon: Tv, title: "Smart TV", steps: "Open the built-in browser on your Smart TV, go to NeTimeTV and add it to your apps / home." },
];

export default function Download() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) { toast.info("Use your browser menu → 'Install app' / 'Add to Home Screen'."); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferred(null);
  };

  return (
    <div className="pt-24 pb-20 px-4 sm:px-8 lg:px-12 min-h-screen relative overflow-hidden" data-testid="download-page">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-brand/20 rounded-full blur-[120px]" />
      <div className="relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/20 text-brand text-xs font-bold uppercase tracking-widest mb-5">
          <DownloadIcon size={14} /> Get the App
        </div>
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold mb-4">Watch NeTimeTV anywhere</h1>
        <p className="text-zinc-400 max-w-xl mx-auto mb-8">Install the NeTimeTV app on your PC, phone or Smart TV for a full-screen, tap-to-launch experience. No app store needed.</p>

        <button data-testid="install-app-btn" onClick={install}
          className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover font-bold px-8 py-4 rounded-xl text-lg transition-colors mb-12">
          {installed ? <><Check size={20} /> Installed</> : <><DownloadIcon size={20} /> Install App</>}
        </button>

        <div className="grid sm:grid-cols-3 gap-4 text-left">
          {PLATFORMS.map((p, i) => (
            <div key={i} className="bg-card border border-white/10 rounded-2xl p-5">
              <span className="w-11 h-11 grid place-items-center rounded-xl bg-white/10 text-brand mb-3"><p.icon size={22} /></span>
              <h3 className="font-head font-bold mb-1">{p.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{p.steps}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
